import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Menu, X, ChevronRight, Phone, Mail, MapPin, Clock, Facebook, Instagram, Twitter, Star } from 'lucide-react';

const Template2 = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categories = [
    { name: 'Fresh Produce', icon: '🥬', color: '#10b981' },
    { name: 'Dairy & Eggs', icon: '🥛', color: '#3b82f6' },
    { name: 'Meat & Seafood', icon: '🥩', color: '#ef4444' },
    { name: 'Bakery', icon: '🍞', color: '#f59e0b' },
    { name: 'Beverages', icon: '🥤', color: '#8b5cf6' },
    { name: 'Snacks', icon: '🍿', color: '#ec4899' },
  ];

  const featuredProducts = [
    { name: 'Fresh Organic Apples', price: '$4.99/lb', image: 'https://images.unsplash.com/photo-1568702846914-96b305d2aaeb?w=400&h=400&fit=crop', rating: 4.5 },
    { name: 'Premium Milk', price: '$3.49', image: 'https://images.unsplash.com/photo-1563636619-e9143da7973b?w=400&h=400&fit=crop', rating: 5 },
    { name: 'Fresh Salmon Fillet', price: '$12.99/lb', image: 'https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=400&h=400&fit=crop', rating: 4.8 },
    { name: 'Artisan Bread', price: '$5.99', image: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400&h=400&fit=crop', rating: 4.7 },
  ];

  const deals = [
    { title: 'Weekend Special', discount: '30% OFF', description: 'All Fresh Vegetables', bgColor: '#10b981' },
    { title: 'Buy 2 Get 1 Free', discount: 'BOGO', description: 'Selected Snacks', bgColor: '#f59e0b' },
    { title: 'Fresh Daily', discount: '20% OFF', description: 'Bakery Items', bgColor: '#ef4444' },
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const addToCart = () => {
    setCartCount(cartCount + 1);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#ffffff',
      color: '#1f2937',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    header: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      transition: 'all 0.3s ease',
      background: scrolled ? 'rgba(255, 255, 255, 0.98)' : '#ffffff',
      boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : '0 2px 10px rgba(0, 0, 0, 0.05)',
    },
    topBar: {
      background: 'linear-gradient(135deg, #10b981, #059669)',
      padding: '0.75rem 2rem',
      color: 'white',
      fontSize: '0.875rem',
    },
    topBarContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    topBarItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    nav: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '1rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logo: {
      fontSize: '1.75rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #10b981, #059669)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    navLinks: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    navButton: {
      background: 'none',
      border: 'none',
      color: '#1f2937',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      fontSize: '1rem',
      fontWeight: '500',
      fontFamily: 'inherit',
    },
    cartButton: {
      position: 'relative',
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
    },
    cartBadge: {
      position: 'absolute',
      top: '-5px',
      right: '-5px',
      background: '#ef4444',
      color: 'white',
      borderRadius: '50%',
      width: '22px',
      height: '22px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.75rem',
      fontWeight: '700',
    },
    menuToggle: {
      display: 'none',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
    },
    heroSection: {
      background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
      padding: '8rem 2rem 4rem',
      marginTop: '6rem',
      position: 'relative',
      overflow: 'hidden',
    },
    heroContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '3rem',
      alignItems: 'center',
    },
    heroText: {
      zIndex: 10,
    },
    heroTitle: {
      fontSize: 'clamp(2.5rem, 6vw, 4rem)',
      fontWeight: '800',
      color: '#065f46',
      marginBottom: '1rem',
      lineHeight: 1.2,
    },
    heroSubtitle: {
      fontSize: '1.25rem',
      color: '#047857',
      marginBottom: '2rem',
      lineHeight: 1.6,
    },
    searchBox: {
      display: 'flex',
      background: 'white',
      borderRadius: '50px',
      padding: '0.5rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      marginBottom: '1.5rem',
    },
    searchInput: {
      flex: 1,
      border: 'none',
      outline: 'none',
      padding: '0.75rem 1.5rem',
      fontSize: '1rem',
      fontFamily: 'inherit',
    },
    searchButton: {
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: 'white',
      border: 'none',
      padding: '0.75rem 2rem',
      borderRadius: '50px',
      cursor: 'pointer',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
    },
    heroImage: {
      position: 'relative',
    },
    heroImg: {
      width: '100%',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(16, 185, 129, 0.3)',
    },
    section: {
      padding: '4rem 2rem',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    sectionTitle: {
      fontSize: 'clamp(2rem, 4vw, 2.5rem)',
      fontWeight: '700',
      color: '#065f46',
      marginBottom: '3rem',
      textAlign: 'center',
    },
    categoriesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1.5rem',
    },
    categoryCard: {
      background: 'white',
      padding: '2rem',
      borderRadius: '15px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: '2px solid #f0fdf4',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
    },
    categoryIcon: {
      fontSize: '3rem',
      marginBottom: '1rem',
      display: 'block',
    },
    categoryName: {
      fontWeight: '600',
      color: '#1f2937',
    },
    productsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '2rem',
    },
    productCard: {
      background: 'white',
      borderRadius: '15px',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    productImage: {
      width: '100%',
      height: '250px',
      objectFit: 'cover',
    },
    productInfo: {
      padding: '1.5rem',
    },
    productName: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '0.5rem',
    },
    productPrice: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#10b981',
      marginBottom: '0.5rem',
    },
    rating: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      color: '#f59e0b',
      marginBottom: '1rem',
    },
    addButton: {
      width: '100%',
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: 'white',
      border: 'none',
      padding: '0.75rem',
      borderRadius: '10px',
      cursor: 'pointer',
      fontWeight: '600',
      transition: 'all 0.3s ease',
    },
    dealsSection: {
      background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
      padding: '4rem 2rem',
    },
    dealsGrid: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '2rem',
    },
    dealCard: {
      background: 'white',
      padding: '2.5rem',
      borderRadius: '20px',
      textAlign: 'center',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      overflow: 'hidden',
    },
    dealBadge: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'white',
      color: '#ef4444',
      padding: '0.5rem 1rem',
      borderRadius: '50px',
      fontWeight: '700',
      fontSize: '0.875rem',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
    },
    dealTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '1rem',
    },
    dealDiscount: {
      fontSize: '3rem',
      fontWeight: '800',
      marginBottom: '0.5rem',
    },
    dealDescription: {
      color: '#6b7280',
      fontSize: '1.1rem',
    },
    contactSection: {
      background: '#f9fafb',
    },
    contactGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '2rem',
    },
    contactCard: {
      background: 'white',
      padding: '2rem',
      borderRadius: '15px',
      textAlign: 'center',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
    },
    contactIcon: {
      width: '60px',
      height: '60px',
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: 'white',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1rem',
    },
    contactTitle: {
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '0.5rem',
    },
    contactText: {
      color: '#6b7280',
    },
    footer: {
      background: '#065f46',
      color: 'white',
      padding: '3rem 2rem 1rem',
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '2rem',
      marginBottom: '2rem',
    },
    footerTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      marginBottom: '1rem',
    },
    footerLink: {
      color: '#d1fae5',
      display: 'block',
      marginBottom: '0.5rem',
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
    },
    socialLinks: {
      display: 'flex',
      gap: '1rem',
      marginTop: '1rem',
    },
    socialIcon: {
      width: '40px',
      height: '40px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    footerBottom: {
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      paddingTop: '1.5rem',
      textAlign: 'center',
      color: '#d1fae5',
    },
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          .nav-button:hover { color: #10b981; }
          .cart-button:hover { transform: scale(1.05); box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3); }
          .search-button:hover { transform: scale(1.05); }
          .category-card:hover { transform: translateY(-10px); box-shadow: 0 10px 30px rgba(16, 185, 129, 0.2); border-color: #10b981; }
          .product-card:hover { transform: translateY(-10px); box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15); }
          .add-button:hover { transform: scale(1.05); }
          .footer-link:hover { color: #ffffff; }
          .social-icon:hover { background: rgba(255, 255, 255, 0.2); transform: translateY(-3px); }
          @media (max-width: 768px) {
            .menu-toggle { display: block !important; }
            .nav-links { display: none !important; }
            .top-bar-content { font-size: 0.75rem; }
          }
        `}
      </style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.topBar}>
          <div style={styles.topBarContent}>
            <div style={styles.topBarItem}>
              <Phone size={16} />
              <span>Call: (555) 123-4567</span>
            </div>
            <div style={styles.topBarItem}>
              <Clock size={16} />
              <span>Mon-Sat: 8AM - 10PM | Sun: 9AM - 8PM</span>
            </div>
          </div>
        </div>
        <nav style={styles.nav}>
          <div style={styles.logo}>
            <ShoppingCart size={32} />
            FRESHMART
          </div>
          <div style={styles.navLinks} className="nav-links">
            <button onClick={() => scrollToSection('home')} style={styles.navButton} className="nav-button">Home</button>
            <button onClick={() => scrollToSection('categories')} style={styles.navButton} className="nav-button">Categories</button>
            <button onClick={() => scrollToSection('products')} style={styles.navButton} className="nav-button">Products</button>
            <button onClick={() => scrollToSection('deals')} style={styles.navButton} className="nav-button">Deals</button>
            <button onClick={() => scrollToSection('contact')} style={styles.navButton} className="nav-button">Contact</button>
            <button style={styles.cartButton} className="cart-button">
              <ShoppingCart size={20} />
              Cart
              {cartCount > 0 && <span style={styles.cartBadge}>{cartCount}</span>}
            </button>
          </div>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} style={styles.menuToggle} className="menu-toggle">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" style={styles.heroSection}>
        <div style={styles.heroContent}>
          <div style={styles.heroText}>
            <h1 style={styles.heroTitle}>Fresh Groceries Delivered to Your Door</h1>
            <p style={styles.heroSubtitle}>
              Shop from thousands of products with guaranteed freshness and quality. Save time and money with our everyday low prices!
            </p>
            <div style={{display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem'}}>
              <span style={{background: 'white', padding: '0.5rem 1rem', borderRadius: '20px', fontSize: '0.875rem', fontWeight: '600'}}>
                ✓ Free Delivery Over $50
              </span>
              <span style={{background: 'white', padding: '0.5rem 1rem', borderRadius: '20px', fontSize: '0.875rem', fontWeight: '600'}}>
                ✓ Same Day Delivery
              </span>
            </div>
          </div>
          <div style={styles.heroImage}>
            <img
              src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=600&h=600&fit=crop"
              alt="Fresh groceries"
              style={styles.heroImg}
            />
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section id="categories" style={styles.section}>
        <h2 style={styles.sectionTitle}>Shop by Category</h2>
        <div style={styles.categoriesGrid}>
          {categories.map((category, index) => (
            <div key={index} style={{...styles.categoryCard, borderColor: category.color + '20'}} className="category-card">
              <span style={styles.categoryIcon}>{category.icon}</span>
              <div style={styles.categoryName}>{category.name}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section id="products" style={styles.section}>
        <h2 style={styles.sectionTitle}>Featured Products</h2>
        <div style={styles.productsGrid}>
          {featuredProducts.map((product, index) => (
            <div key={index} style={styles.productCard} className="product-card">
              <img src={product.image} alt={product.name} style={styles.productImage} />
              <div style={styles.productInfo}>
                <h3 style={styles.productName}>{product.name}</h3>
                <div style={styles.rating}>
                  <Star size={16} fill="#f59e0b" />
                  <span style={{color: '#1f2937', fontWeight: '600'}}>{product.rating}</span>
                </div>
                <div style={styles.productPrice}>{product.price}</div>
                <button onClick={addToCart} style={styles.addButton} className="add-button">
                  Add to Cart
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Deals Section */}
      <section id="deals" style={styles.dealsSection}>
        <h2 style={{...styles.sectionTitle, color: '#92400e'}}>Today's Special Deals</h2>
        <div style={styles.dealsGrid}>
          {deals.map((deal, index) => (
            <div key={index} style={styles.dealCard}>
              <div style={styles.dealBadge}>HOT DEAL</div>
              <h3 style={styles.dealTitle}>{deal.title}</h3>
              <div style={{...styles.dealDiscount, color: deal.bgColor}}>{deal.discount}</div>
              <p style={styles.dealDescription}>{deal.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{...styles.section, ...styles.contactSection}}>
        <h2 style={styles.sectionTitle}>Visit Us Today</h2>
        <div style={styles.contactGrid}>
          <div style={styles.contactCard}>
            <div style={styles.contactIcon}>
              <MapPin size={28} />
            </div>
            <h3 style={styles.contactTitle}>Location</h3>
            <p style={styles.contactText}>123 Market Street<br/>Fresh City, FC 12345</p>
          </div>
          <div style={styles.contactCard}>
            <div style={styles.contactIcon}>
              <Phone size={28} />
            </div>
            <h3 style={styles.contactTitle}>Phone</h3>
            <p style={styles.contactText}>(555) 123-4567<br/>Mon-Sat: 8AM - 10PM</p>
          </div>
          <div style={styles.contactCard}>
            <div style={styles.contactIcon}>
              <Mail size={28} />
            </div>
            <h3 style={styles.contactTitle}>Email</h3>
            <p style={styles.contactText}>info@freshmart.com<br/>support@freshmart.com</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div>
            <h4 style={styles.footerTitle}>FRESHMART</h4>
            <p style={{color: '#d1fae5', lineHeight: 1.6}}>
              Your trusted neighborhood supermarket for fresh, quality groceries at affordable prices.
            </p>
            <div style={styles.socialLinks}>
              <div style={styles.socialIcon} className="social-icon">
                <Facebook size={20} />
              </div>
              <div style={styles.socialIcon} className="social-icon">
                <Instagram size={20} />
              </div>
              <div style={styles.socialIcon} className="social-icon">
                <Twitter size={20} />
              </div>
            </div>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Quick Links</h4>
            <a onClick={() => scrollToSection('home')} style={styles.footerLink} className="footer-link">Home</a>
            <a onClick={() => scrollToSection('categories')} style={styles.footerLink} className="footer-link">Categories</a>
            <a onClick={() => scrollToSection('products')} style={styles.footerLink} className="footer-link">Products</a>
            <a onClick={() => scrollToSection('deals')} style={styles.footerLink} className="footer-link">Deals</a>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Customer Service</h4>
            <a style={styles.footerLink} className="footer-link">FAQ</a>
            <a style={styles.footerLink} className="footer-link">Shipping Info</a>
            <a style={styles.footerLink} className="footer-link">Returns</a>
            <a style={styles.footerLink} className="footer-link">Track Order</a>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Store Hours</h4>
            <p style={{color: '#d1fae5', marginBottom: '0.5rem'}}>Monday - Saturday</p>
            <p style={{color: '#d1fae5', marginBottom: '1rem'}}>8:00 AM - 10:00 PM</p>
            <p style={{color: '#d1fae5', marginBottom: '0.5rem'}}>Sunday</p>
            <p style={{color: '#d1fae5'}}>9:00 AM - 8:00 PM</p>
          </div>
        </div>
        <div style={styles.footerBottom}>
          <p>&copy; 2025 FreshMart. All rights reserved.</p>
          <Link to="/build-your-website" style={{...styles.ctaButton, marginTop: '1rem', display: 'inline-block', textDecoration: 'none'}}>Go Back</Link>
        </div>
      </footer>
    </div>
  );
};

export default Template2;